/**
 * Accountant Marketplace
 * Browse and search for accountants to work with
 */

import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import {
  Search,
  Star,
  MapPin,
  Building2,
  CheckCircle2,
  Users,
  Award,
  Briefcase,
  Filter,
  ChevronRight,
} from "lucide-react";
import { Link } from "wouter";

// Swiss cantons
const SWISS_CANTONS = [
  { value: "ZH", label: "Zürich" },
  { value: "BE", label: "Bern" },
  { value: "LU", label: "Luzern" },
  { value: "UR", label: "Uri" },
  { value: "SZ", label: "Schwyz" },
  { value: "OW", label: "Obwalden" },
  { value: "NW", label: "Nidwalden" },
  { value: "GL", label: "Glarus" },
  { value: "ZG", label: "Zug" },
  { value: "FR", label: "Fribourg" },
  { value: "SO", label: "Solothurn" },
  { value: "BS", label: "Basel-Stadt" },
  { value: "BL", label: "Basel-Landschaft" },
  { value: "SH", label: "Schaffhausen" },
  { value: "AR", label: "Appenzell A.Rh." },
  { value: "AI", label: "Appenzell I.Rh." },
  { value: "SG", label: "St. Gallen" },
  { value: "GR", label: "Graubünden" },
  { value: "AG", label: "Aargau" },
  { value: "TG", label: "Thurgau" },
  { value: "TI", label: "Ticino" },
  { value: "VD", label: "Vaud" },
  { value: "VS", label: "Valais" },
  { value: "NE", label: "Neuchâtel" },
  { value: "GE", label: "Genève" },
  { value: "JU", label: "Jura" },
];

const SPECIALIZATIONS = [
  { value: "startups", label: "Startups & Scale-ups" },
  { value: "r&d", label: "R&D Tax Credits" },
  { value: "international", label: "International Tax" },
  { value: "vat", label: "VAT Specialist" },
  { value: "payroll", label: "Payroll & HR" },
  { value: "audit", label: "Audit Services" },
  { value: "succession", label: "Business Succession" },
  { value: "real-estate", label: "Real Estate" },
];

const CERTIFICATIONS = [
  { value: "expert_suisse", label: "Expert Suisse" },
  { value: "treuhand_suisse", label: "Treuhand Suisse" },
  { value: "cpa", label: "CPA" },
  { value: "swiss_gaap", label: "Swiss GAAP FER" },
];

export default function AccountantMarketplace() {
  return (
    <DashboardLayout>
      <MarketplaceContent />
    </DashboardLayout>
  );
}

function MarketplaceContent() {
  const { isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCanton, setSelectedCanton] = useState<string>("");
  const [selectedSpecializations, setSelectedSpecializations] = useState<
    string[]
  >([]);
  const [selectedCertifications, setSelectedCertifications] = useState<
    string[]
  >([]);
  const [acceptingOnly, setAcceptingOnly] = useState(true);
  const [minRating, setMinRating] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  // Search query
  const { data, isLoading } = trpc.accountant.search.useQuery({
    query: searchQuery || undefined,
    cantons: selectedCanton ? [selectedCanton] : undefined,
    specializations:
      selectedSpecializations.length > 0 ? selectedSpecializations : undefined,
    certifications:
      selectedCertifications.length > 0 ? selectedCertifications : undefined,
    minRating,
    acceptingClientsOnly: acceptingOnly,
    limit: 20,
  });

  const toggleSpecialization = (spec: string) => {
    setSelectedSpecializations(prev =>
      prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]
    );
  };

  const toggleCertification = (cert: string) => {
    setSelectedCertifications(prev =>
      prev.includes(cert) ? prev.filter(c => c !== cert) : [...prev, cert]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Find an Accountant
          </h1>
          <p className="text-muted-foreground">
            Browse certified Swiss accountants and tax advisors
          </p>
        </div>
        {isAuthenticated && (
          <Link href="/accountant/register">
            <Button variant="outline">
              <Briefcase className="mr-2 h-4 w-4" />
              Become an Accountant
            </Button>
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by firm name or expertise..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select
                value={selectedCanton || "__all__"}
                onValueChange={v => setSelectedCanton(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="w-[180px]">
                  <MapPin className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="All Cantons" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Cantons</SelectItem>
                  {SWISS_CANTONS.map(canton => (
                    <SelectItem key={canton.value} value={canton.value}>
                      {canton.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {(selectedSpecializations.length > 0 ||
                  selectedCertifications.length > 0 ||
                  minRating) && (
                  <Badge variant="secondary" className="ml-2">
                    {selectedSpecializations.length +
                      selectedCertifications.length +
                      (minRating ? 1 : 0)}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="grid gap-4 md:grid-cols-3 pt-4 border-t">
                {/* Specializations */}
                <div className="space-y-2">
                  <Label>Specializations</Label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALIZATIONS.map(spec => (
                      <Badge
                        key={spec.value}
                        variant={
                          selectedSpecializations.includes(spec.value)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer"
                        onClick={() => toggleSpecialization(spec.value)}
                      >
                        {spec.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div className="space-y-2">
                  <Label>Certifications</Label>
                  <div className="flex flex-wrap gap-2">
                    {CERTIFICATIONS.map(cert => (
                      <Badge
                        key={cert.value}
                        variant={
                          selectedCertifications.includes(cert.value)
                            ? "default"
                            : "outline"
                        }
                        className="cursor-pointer"
                        onClick={() => toggleCertification(cert.value)}
                      >
                        {cert.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Other Filters */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Minimum Rating</Label>
                    <Select
                      value={minRating?.toString() || "__any__"}
                      onValueChange={v =>
                        setMinRating(
                          v && v !== "__any__" ? Number(v) : undefined
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any rating" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any__">Any rating</SelectItem>
                        <SelectItem value="4">4+ stars</SelectItem>
                        <SelectItem value="4.5">4.5+ stars</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="accepting"
                      checked={acceptingOnly}
                      onCheckedChange={checked =>
                        setAcceptingOnly(checked as boolean)
                      }
                    />
                    <label
                      htmlFor="accepting"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Only show accountants accepting new clients
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {isLoading
              ? "Searching..."
              : `${data?.total || 0} accountants found`}
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-24 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data?.accountants && data.accountants.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {data.accountants.map(accountant => (
              <AccountantCard key={accountant.id} accountant={accountant} />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No accountants found
              </h3>
              <p className="text-sm text-muted-foreground text-center max-w-md">
                Try adjusting your search filters or browse all accountants by
                clearing your search.
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCanton("");
                  setSelectedSpecializations([]);
                  setSelectedCertifications([]);
                  setMinRating(undefined);
                }}
              >
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

interface AccountantCardProps {
  accountant: {
    id: number;
    firmName: string;
    firmType: string | null;
    bio: string | null;
    website: string | null;
    cantons: string[] | null;
    specializations: string[] | null;
    certifications: string[] | null;
    clientCount: number | null;
    avgRating: number;
    reviewCount: number | null;
    isVerified: boolean | null;
    acceptingClients: boolean | null;
  };
}

function AccountantCard({ accountant }: AccountantCardProps) {
  return (
    <Link href={`/accountant/${accountant.id}`}>
      <Card className="cursor-pointer transition-shadow hover:shadow-md">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">
                    {accountant.firmName}
                  </CardTitle>
                  {accountant.isVerified && (
                    <CheckCircle2 className="h-4 w-4 text-success" />
                  )}
                </div>
                <CardDescription className="flex items-center gap-2">
                  {accountant.firmType && (
                    <span className="capitalize">{accountant.firmType}</span>
                  )}
                  {accountant.cantons && accountant.cantons.length > 0 && (
                    <>
                      <span>•</span>
                      <span>{accountant.cantons.slice(0, 3).join(", ")}</span>
                      {accountant.cantons.length > 3 && (
                        <span>+{accountant.cantons.length - 3}</span>
                      )}
                    </>
                  )}
                </CardDescription>
              </div>
            </div>
            {accountant.acceptingClients && (
              <Badge variant="secondary" className="bg-success/10 text-success">
                Accepting Clients
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {accountant.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {accountant.bio}
            </p>
          )}

          {/* Specializations */}
          {accountant.specializations &&
            accountant.specializations.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {accountant.specializations.slice(0, 4).map(spec => (
                  <Badge key={spec} variant="outline" className="text-xs">
                    {SPECIALIZATIONS.find(s => s.value === spec)?.label || spec}
                  </Badge>
                ))}
                {accountant.specializations.length > 4 && (
                  <Badge variant="outline" className="text-xs">
                    +{accountant.specializations.length - 4}
                  </Badge>
                )}
              </div>
            )}

          {/* Stats */}
          <div className="flex items-center justify-between pt-3 border-t">
            <div className="flex items-center gap-4">
              {accountant.avgRating > 0 && (
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-[var(--warning)] text-warning" />
                  <span className="font-medium">
                    {accountant.avgRating.toFixed(1)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    ({accountant.reviewCount || 0})
                  </span>
                </div>
              )}
              {accountant.clientCount !== null &&
                accountant.clientCount > 0 && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{accountant.clientCount} clients</span>
                  </div>
                )}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>

          {/* Certifications */}
          {accountant.certifications &&
            accountant.certifications.length > 0 && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <Award className="h-4 w-4 text-muted-foreground" />
                <div className="flex flex-wrap gap-1">
                  {accountant.certifications.map(cert => (
                    <span key={cert} className="text-xs text-muted-foreground">
                      {CERTIFICATIONS.find(c => c.value === cert)?.label ||
                        cert}
                    </span>
                  ))}
                </div>
              </div>
            )}
        </CardContent>
      </Card>
    </Link>
  );
}
